import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FolderArrowDownIcon,
  FolderPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { type MoveRequest, sanitizeDestFolder } from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { enqueueMove } from './organize-api';
import { useAllFolders } from './use-all-folders';
import { buildFolderTree, composeNewFolderPath, type FolderNode } from './folder-tree';
import { FolderTreePicker, type FolderTreePickerLabels } from './FolderTreePicker';

/**
 * Modale de DÉPLACEMENT de médias vers un dossier (LOT 2, refonte UX v0.136).
 *
 * Deux modes de cible mutuellement exclusifs :
 *  - `{ hasAudio: true }`   : toutes les vidéos avec son (résolues côté serveur).
 *  - `{ assetIds: [...] }`  : la sélection de tuiles courante.
 *
 * Destination : UN SEUL arbre de dossiers (la racine, sélectionnable, est proposée en
 * tête). Un BANDEAU récapitule la destination choisie et propose « Nouveau dossier ici » :
 * une carte de confirmation inline crée un sous-dossier DANS la destination courante (nom
 * validé en direct par `sanitizeDestFolder`, même règle que le serveur). Le dossier est
 * créé IMPLICITEMENT au moment du déplacement — aucun appel de création séparé. Par
 * défaut la destination est `with_sound` (dossier neuf, absent de l'arbre → le picker ne
 * surligne rien, c'est attendu ; le serveur le crée au déplacement).
 *
 * Boîte de dialogue accessible : `role="dialog"` + `aria-modal`, focus initial/restauré,
 * verrou de défilement, Échap ferme. Le caller l'enveloppe dans un `<Portal>`.
 */
export interface MoveTarget {
  /** Déplace toutes les vidéos avec son (résolution serveur). */
  hasAudio?: true;
  /** Déplace ces assets précis. */
  assetIds?: string[];
}

interface MoveToFolderModalProps {
  libraryId: string;
  target: MoveTarget;
  onClose: () => void;
  /** Appelé après un enfilement réussi (p.ex. vider la sélection + rafraîchir). */
  onQueued?: () => void;
}

/** Destination suggérée par défaut (le retour utilisateur cite « with_sound »). */
const DEFAULT_DEST = 'with_sound';

export function MoveToFolderModal({
  libraryId,
  target,
  onClose,
  onQueued,
}: MoveToFolderModalProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const closeRef = useRef<HTMLButtonElement>(null);
  const newNameInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Destination sélectionnée. Source de vérité UNIQUE : alimente le submit ET le
  // `selected` du picker. Un dossier « nouveau » (absent de l'arbre) y est aussi posé.
  const [dest, setDest] = useState(DEFAULT_DEST);
  // Carte « Nouveau dossier ici » : ouverte ? + nom du sous-dossier saisi.
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newName, setNewName] = useState('');
  // Arbre des dossiers PARTAGÉ (audit A9) : rouvrir la modale ne re-liste plus tout.
  const folders = useAllFolders(libraryId);
  const [busy, setBusy] = useState(false);
  useModalA11y({ dialogRef, initialFocusRef: closeRef, onEscape: !busy ? onClose : undefined });
  const [error, setError] = useState<'writeDisabled' | 'generic' | null>(null);
  const [done, setDone] = useState(false);

  // Destination effective (miroir client de la règle serveur) : la racine (`''`) est valide.
  const destNormalized = dest === '' ? '' : sanitizeDestFolder(dest);
  const destValid = destNormalized !== null;
  // Libellé affiché de la destination : le chemin, ou le libellé racine si racine.
  const destDisplay = dest === '' ? t('folders.root') : dest;

  // Carte de création : chemin composé (parent = destination courante) + version normalisée.
  const newRawPath = composeNewFolderPath(dest, newName);
  const newNormalized = newRawPath === '' ? null : sanitizeDestFolder(newRawPath);
  const newNameEmpty = newName.trim() === '';

  const byIds = target.assetIds !== undefined && target.assetIds.length > 0;
  const selectionCount = target.assetIds?.length ?? 0;

  // Verrou de défilement + focus initial + restauration.

  // À l'ouverture de la carte, place le focus dans le champ « nom ».
  useEffect(() => {
    if (creatingFolder) newNameInputRef.current?.focus();
  }, [creatingFolder]);

  // Échap ferme (sauf pendant l'appel réseau : on n'interrompt pas une action en cours).

  const tree: FolderNode[] = useMemo(
    () => (folders.status === 'ready' ? buildFolderTree(folders.entries) : []),
    [folders],
  );

  // Libellés du sélecteur de dossier repliable (mémorisés — dépendent de la locale).
  const pickerLabels: FolderTreePickerLabels = useMemo(
    () => ({
      search: t('organize.searchPlaceholder'),
      loading: t('organize.loadingFolders'),
      error: t('organize.foldersError'),
      root: t('folders.root'),
      selectFolder: t('organize.selectFolder'),
      folderCount: t('organize.folderCount'),
      expand: t('organize.expandFolder'),
      collapse: t('organize.collapseFolder'),
      searchEmpty: t('organize.searchEmpty'),
    }),
    [t],
  );

  /** Ouvre la carte de création d'un sous-dossier (nom vierge). */
  const openCreateFolder = useCallback((): void => {
    setNewName('');
    setCreatingFolder(true);
  }, []);

  /** Ferme la carte SANS rien changer à la destination. */
  const cancelCreateFolder = useCallback((): void => {
    setCreatingFolder(false);
    setNewName('');
  }, []);

  /** Valide la carte : pose la destination = nouveau chemin normalisé, puis referme. */
  const confirmCreateFolder = useCallback((): void => {
    if (newNormalized === null) return;
    setDest(newNormalized);
    setCreatingFolder(false);
    setNewName('');
  }, [newNormalized]);

  const onSubmit = useCallback(async () => {
    if (!destValid || destNormalized === null || busy) return;
    setBusy(true);
    setError(null);
    // Corps typé : mode ids OU mode hasAudio (jamais les deux). On envoie la
    // destination NORMALISÉE (le serveur la re-valide de toute façon).
    const body: MoveRequest = byIds
      ? { destFolder: destNormalized, dryRun: false, assetIds: target.assetIds ?? [] }
      : { destFolder: destNormalized, dryRun: false, hasAudio: true };
    try {
      await enqueueMove(libraryId, body);
      setDone(true);
      onQueued?.();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setError('writeDisabled');
      } else {
        setError('generic');
      }
    } finally {
      setBusy(false);
    }
  }, [destValid, destNormalized, busy, byIds, target.assetIds, libraryId, onQueued]);

  const intro = byIds
    ? interpolate(t('organize.moveSelectionIntro'), { count: selectionCount }, locale)
    : t('organize.moveWithAudioIntro');

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('organize.moveTitle')}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <FolderArrowDownIcon className="h-6 w-6 text-brand" aria-hidden="true" />
            {t('organize.moveTitle')}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('organize.close')}
            aria-label={t('organize.close')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Corps. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          {done ? (
            <p
              role="status"
              className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-fg"
            >
              <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              {t('organize.success')}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted">{intro}</p>

              {/* Destination : UN SEUL arbre + bandeau récap + carte « Nouveau dossier ici ». */}
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-fg">{t('organize.destLabel')}</p>

                <FolderTreePicker
                  status={folders.status}
                  nodes={tree}
                  selected={destNormalized}
                  onSelect={(path) => setDest(path)}
                  busy={busy}
                  enlarged={false}
                  labels={pickerLabels}
                />

                {/* Bandeau : destination sélectionnée + action « Nouveau dossier ici ». */}
                <div className="flex items-center justify-between gap-3 rounded-md bg-surface-2 p-3">
                  <div className="min-w-0">
                    <p className="text-xs text-muted">{t('organize.destSelected')}</p>
                    <p className="truncate text-sm font-medium text-fg" title={destDisplay}>
                      {destDisplay}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openCreateFolder}
                    disabled={busy}
                    className="inline-flex shrink-0 items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                  >
                    <FolderPlusIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {t('organize.newFolderHere')}
                  </button>
                </div>

                {/* Carte de confirmation inline : créer un sous-dossier DANS la destination. */}
                {creatingFolder && (
                  <div className="flex flex-col gap-3 rounded-md border border-brand bg-brand/10 p-3">
                    <p
                      id="move-newfolder-question"
                      className="inline-flex items-start gap-2 text-sm font-medium text-fg"
                    >
                      <FolderPlusIcon
                        className="mt-0.5 h-5 w-5 shrink-0 text-brand"
                        aria-hidden="true"
                      />
                      {interpolate(t('organize.newFolderConfirm'), { parent: destDisplay })}
                    </p>

                    <div className="flex flex-col gap-1">
                      <label htmlFor="move-newname-input" className="text-sm font-medium text-fg">
                        {t('organize.newFolderLabel')}
                      </label>
                      <input
                        id="move-newname-input"
                        ref={newNameInputRef}
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        disabled={busy}
                        autoComplete="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        placeholder={t('organize.destPlaceholder')}
                        aria-invalid={!newNameEmpty && newNormalized === null}
                        aria-describedby="move-newfolder-question move-newname-preview"
                        className="h-11 rounded-md border border-border bg-surface px-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                      />
                      {/* Aperçu vivant du chemin résultant OU message d'erreur OU hint. */}
                      {newNameEmpty ? (
                        <span id="move-newname-preview" className="text-xs text-muted">
                          {t('organize.destHint')}
                        </span>
                      ) : newNormalized !== null ? (
                        <span
                          id="move-newname-preview"
                          className="inline-flex items-center gap-1 text-xs text-muted"
                        >
                          <FolderPlusIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                          {interpolate(t('organize.newFolderPreview'), { path: newNormalized })}
                        </span>
                      ) : (
                        <p
                          id="move-newname-preview"
                          className="inline-flex items-center gap-1 text-sm text-danger"
                        >
                          <ExclamationTriangleIcon
                            className="h-5 w-5 shrink-0"
                            aria-hidden="true"
                          />
                          {t('organize.destInvalid')}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={cancelCreateFolder}
                        disabled={busy}
                        className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                      >
                        {t('organize.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={confirmCreateFolder}
                        disabled={busy || newNormalized === null}
                        className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <FolderPlusIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        {t('organize.newFolderCreate')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {error !== null && (
                <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {error === 'writeDisabled'
                    ? t('organize.errorWriteDisabled')
                    : t('organize.error')}
                </p>
              )}
            </>
          )}
        </div>

        {/* Actions. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          {done ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('organize.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                {t('organize.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void onSubmit()}
                disabled={!destValid || busy}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy && (
                  <ArrowPathIcon
                    className="h-5 w-5 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                {busy ? t('organize.submitting') : t('organize.submit')}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
